'use client';

import { useState, useRef } from 'react';
import { ImagePlus, X, Loader2 } from 'lucide-react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import { compressReviewImage } from '@/lib/image-compress';

export interface PendingImage {
  file: File;
  previewUrl: string;
}

interface ReviewImageUploadProps {
  existingImages: string[];
  pendingImages: PendingImage[];
  maxImages?: number;
  onAddFiles: (files: File[]) => void;
  onRemoveExisting: (index: number) => void;
  onRemovePending: (index: number) => void;
  disabled?: boolean;
  compact?: boolean;
}

export default function ReviewImageUpload({
  existingImages,
  pendingImages,
  maxImages = 5,
  onAddFiles,
  onRemoveExisting,
  onRemovePending,
  disabled = false,
  compact = false,
}: ReviewImageUploadProps) {
  const t = useTranslations('reviews');
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const totalImages = existingImages.length + pendingImages.length;
  const canAddMore = totalImages < maxImages && !isProcessing;

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const remainingSlots = maxImages - totalImages;
    if (remainingSlots <= 0) {
      setError(t('maxImagesError', { max: maxImages }));
      return;
    }

    const filesToAdd = Array.from(files).slice(0, remainingSlots);

    // 파일 유효성 검사
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    for (const file of filesToAdd) {
      if (!allowedTypes.includes(file.type)) {
        setError(t('uploadError'));
        return;
      }
      if (file.size > maxSize) {
        setError(t('uploadError'));
        return;
      }
    }

    setError(null);
    setIsProcessing(true);
    try {
      // 업로드 전에 리사이즈/압축 — 원본 8MB이 1MB 수준으로 줄어 네트워크 시간 단축.
      // 병렬 처리해서 여러 장이어도 가장 느린 한 장의 시간만 소요.
      const compressed = await Promise.all(
        filesToAdd.map((f) => compressReviewImage(f)),
      );
      onAddFiles(compressed);
    } catch (err) {
      console.error('[Review] Image compression error:', err);
      // 압축 실패 시 원본 그대로 전달 (업로드는 가능)
      onAddFiles(filesToAdd);
    } finally {
      setIsProcessing(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept="image/jpeg,image/png,image/webp"
      multiple
      onChange={handleFileSelect}
      disabled={disabled}
      className="sr-only"
    />
  );

  // 컴팩트 모드: 카메라 아이콘 + 인라인 미리보기
  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {/* 기존 업로드된 이미지 */}
        {existingImages.map((url, index) => (
          <div
            key={`existing-${index}`}
            className="relative w-8 h-8 rounded overflow-hidden border border-gray-200"
          >
            <Image
              src={url}
              alt={t('imageAlt', { index: index + 1 })}
              fill
              className="object-cover"
              sizes="32px"
            />
            {!disabled && (
              <button
                type="button"
                onClick={() => onRemoveExisting(index)}
                className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
                aria-label={t('removeImage')}
              >
                <X className="w-3 h-3 text-white" />
              </button>
            )}
          </div>
        ))}

        {/* 로컬 미리보기 이미지 */}
        {pendingImages.map((img, index) => (
          <div
            key={`pending-${index}`}
            className="relative w-8 h-8 rounded overflow-hidden border border-gray-200"
          >
            <Image
              src={img.previewUrl}
              alt={t('imageAlt', { index: existingImages.length + index + 1 })}
              fill
              className="object-cover"
              sizes="32px"
              unoptimized
            />
            {!disabled && (
              <button
                type="button"
                onClick={() => onRemovePending(index)}
                className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
                aria-label={t('removeImage')}
              >
                <X className="w-3 h-3 text-white" />
              </button>
            )}
          </div>
        ))}

        {/* 카메라 아이콘 업로드 버튼 */}
        {canAddMore && !disabled && (
          <label className="cursor-pointer p-1 hover:bg-gray-100 rounded transition-colors">
            {fileInput}
            {isProcessing ? (
              <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
            ) : (
              <ImagePlus className="w-5 h-5 text-gray-400" />
            )}
          </label>
        )}

        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>
    );
  }

  // 기본 모드: 풀사이즈 썸네일 그리드
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {/* 기존 업로드된 이미지 */}
        {existingImages.map((url, index) => (
          <div
            key={`existing-${index}`}
            className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200"
          >
            <Image
              src={url}
              alt={t('imageAlt', { index: index + 1 })}
              fill
              className="object-cover"
              sizes="80px"
            />
            {!disabled && (
              <button
                type="button"
                onClick={() => onRemoveExisting(index)}
                className="absolute top-1 right-1 p-0.5 bg-black/60 rounded-full text-white hover:bg-black/80 transition-colors"
                aria-label={t('removeImage')}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}

        {/* 로컬 미리보기 이미지 */}
        {pendingImages.map((img, index) => (
          <div
            key={`pending-${index}`}
            className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200"
          >
            <Image
              src={img.previewUrl}
              alt={t('imageAlt', { index: existingImages.length + index + 1 })}
              fill
              className="object-cover"
              sizes="80px"
              unoptimized
            />
            {!disabled && (
              <button
                type="button"
                onClick={() => onRemovePending(index)}
                className="absolute top-1 right-1 p-0.5 bg-black/60 rounded-full text-white hover:bg-black/80 transition-colors"
                aria-label={t('removeImage')}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}

        {/* 업로드 버튼 */}
        {canAddMore && !disabled && (
          <label
            className={cn(
              'w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer transition-colors',
              'hover:border-orange-400 hover:bg-orange-50'
            )}
          >
            {fileInput}
            {isProcessing ? (
              <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
            ) : (
              <ImagePlus className="w-5 h-5 text-gray-400" />
            )}
            <span className="text-xs text-gray-400 mt-1">
              {totalImages}/{maxImages}
            </span>
          </label>
        )}
      </div>

      {/* 에러 메시지 */}
      {error && <p className="text-sm text-red-500">{error}</p>}

      {/* 안내 문구 */}
      <p className="text-xs text-gray-500">
        {t('imageFormats', { max: maxImages })}
      </p>
    </div>
  );
}
